import React from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';

const styles = {
  page: {
    background: '#f7f7f7',
    minHeight: '100vh',
    fontFamily: 'Arial, sans-serif',
    color: '#1f2937',
  },
  headerWrap: {
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  container: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '24px 20px 60px',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '20px',
  },
  backButton: {
    border: 'none',
    background: '#111827',
    color: '#fff',
    padding: '10px 18px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  card: {
    background: '#ffffff',
    borderRadius: '18px',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
    padding: '32px 28px',
  },
  title: {
    margin: '0 0 18px',
    fontSize: '34px',
    fontWeight: '700',
    color: '#111827',
  },
  section: {
    marginTop: '28px',
  },
  sectionTitle: {
    fontSize: '28px',
    margin: '0 0 14px',
    color: '#111827',
  },
  paragraph: {
    margin: '0 0 14px',
    fontSize: '16px',
    lineHeight: 1.8,
    color: '#374151',
  },
  list: {
    margin: '0 0 14px 20px',
    padding: 0,
    color: '#374151',
    lineHeight: 1.8,
    fontSize: '16px',
  },
  strongText: {
    fontWeight: 700,
    color: '#111827',
  },
  commitment: {
    marginTop: '28px',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb',
    fontSize: '17px',
    lineHeight: 1.8,
    color: '#374151',
  },
};

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <div style={styles.headerWrap}>
        <Header />
      </div>

      <div style={styles.container}>
        <div style={styles.topBar}>
          <button style={styles.backButton} onClick={() => navigate('/')}>
            ← Back
          </button>
        </div>

        <div style={styles.card}>
          <h1 style={styles.title}>Terms & Conditions</h1>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>1. Scope of Service</h2>
            <p style={styles.paragraph}>
              MyHelpz provides on-demand human assistance services across various categories,
              including household support, shopping assistance, travel assistance, hospital
              support, event assistance, companionship, errands, and other personal support
              services within designated service areas.
            </p>
            <p style={styles.paragraph}>
              MyHelpz Assistants provide assistance based on the service selected and agreed
              upon at the time of booking. Assistants reserve the right to decline tasks that
              are illegal, unsafe, hazardous, physically unreasonable, or beyond the scope of
              the booked service.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>2. Verification &amp; Training</h2>
            <p style={styles.paragraph}>
              All MyHelpz Assistants undergo identity verification, background screening,
              onboarding, and service-specific training processes where applicable.
            </p>
            <p style={styles.paragraph}>
              Our Assistants are expected to maintain professional conduct, respect customer
              privacy, and provide services responsibly and courteously.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>3. Customer Responsibility</h2>
            <p style={styles.paragraph}>Customers are expected to:</p>
            <ul style={styles.list}>
              <li>Clearly communicate service requirements and instructions.</li>
              <li>Provide accurate booking information and service locations.</li>
              <li>Avoid requesting any illegal, unethical, unsafe, or prohibited activities.</li>
              <li>Inform the Assistant of any fragile, valuable, sensitive, or special-handling items.</li>
              <li>Ensure a safe environment for service delivery.</li>
            </ul>
            <p style={styles.paragraph}>
              Failure to comply may result in cancellation or refusal of service.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>4. Safety of Belongings</h2>
            <p style={styles.paragraph}>
              MyHelpz Assistants exercise reasonable care while assisting customers. However,
              customers are strongly advised not to hand over cash, jewellery, confidential
              documents, mobile phones, financial instruments, or other high-value personal
              belongings unless explicitly covered under a specific service agreement.
            </p>
            <p style={styles.paragraph}>
              MyHelpz shall not be responsible for loss or damage arising from undisclosed
              valuables or improper handling instructions.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>5. Shopping, Queue &amp; Errand Assistance</h2>
            <p style={styles.paragraph}>MyHelpz Assistants may assist with:</p>
            <ul style={styles.list}>
              <li>Shopping support</li>
              <li>Carrying purchases</li>
              <li>Queue management</li>
              <li>Bill payments</li>
              <li>Order collection</li>
              <li>Grocery and personal errands</li>
              <li>Local assistance tasks</li>
            </ul>
            <p style={styles.paragraph}>
              Any purchases, food orders, products, tickets, or third-party services must be
              paid for directly by the customer unless otherwise specified within the booking.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>6. Child, Elderly &amp; Dependent Assistance Disclaimer</h2>
            <p style={styles.paragraph}>
              MyHelpz may provide assistance to customers accompanying children, senior
              citizens, or dependent individuals.
            </p>
            <p style={styles.paragraph}>
              Unless specifically stated under a dedicated caregiving or supervision service:
            </p>
            <ul style={styles.list}>
              <li>MyHelpz Assistants are not guardians, babysitters, nurses, or medical professionals.</li>
              <li>Parents, guardians, or authorized caregivers remain fully responsible for children and dependents at all times.</li>
              <li>Assistants may provide supportive assistance only within the scope of the selected service.</li>
            </ul>
            <p style={styles.paragraph}>
              MyHelpz shall not be liable for incidents, injuries, medical emergencies, or
              damages arising from activities involving children, elderly persons, or dependents.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>7. Service Duration &amp; Extensions</h2>
            <p style={styles.paragraph}>
              Services are provided strictly according to the selected service package, booking
              duration, or agreed schedule.
            </p>
            <p style={styles.paragraph}>Customers may request extensions, subject to:</p>
            <ul style={styles.list}>
              <li>Assistant availability</li>
              <li>Operational feasibility</li>
              <li>Additional service charges</li>
            </ul>
            <p style={styles.paragraph}>
              Optional add-on services, equipment, or special requests may be available at
              additional charges communicated before service delivery.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>8. Limitations of Liability</h2>
            <p style={styles.paragraph}>
              While MyHelpz takes reasonable steps to ensure service quality, safety, and
              reliability, MyHelpz shall not be liable for:
            </p>
            <ul style={styles.list}>
              <li>Delays caused by traffic, weather, crowd conditions, or other external factors.</li>
              <li>Losses resulting from inaccurate booking information provided by customers.</li>
              <li>Damage to undisclosed fragile or high-value items.</li>
              <li>Service interruptions beyond our reasonable control.</li>
              <li>Actions, omissions, or decisions made by third-party vendors or establishments.</li>
              <li>Indirect, incidental, special, or consequential damages.</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>9. Code of Conduct</h2>
            <p style={styles.paragraph}>
              MyHelpz maintains a strict code of professional conduct for both customers and
              Assistants.
            </p>
            <p style={styles.paragraph}>The following are strictly prohibited:</p>
            <ul style={styles.list}>
              <li>Harassment or abusive behavior</li>
              <li>Discrimination of any kind</li>
              <li>Threatening conduct</li>
              <li>Illegal activities</li>
              <li>Unsafe requests</li>
              <li>Misuse of the platform</li>
            </ul>
            <p style={styles.paragraph}>
              Any violation may result in immediate suspension, termination of service, or
              legal action where applicable.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>10. Payments &amp; Invoices</h2>
            <p style={styles.paragraph}>
              All service charges must be paid through authorized payment methods provided by
              MyHelpz.
            </p>
            <p style={styles.paragraph}>
              Customers will receive payment confirmations, invoices, or receipts in accordanc
              with applicable regulations.
            </p>
            <p style={styles.paragraph}>
              Unless specifically authorized by MyHelpz, Assistants are not permitted to collect
              payments directly from customers.
            </p>
            <p style={styles.paragraph}>Additional charges may apply for:</p>
            <ul style={styles.list}>
              <li>Service extensions</li>
              <li>Additional tasks</li>
              <li>Waiting time</li>
              <li>Add-on services</li>
              <li>Out-of-scope requests</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>11. Service Availability</h2>
            <p style={styles.paragraph}>Services are subject to:</p>
            <ul style={styles.list}>
              <li>Assistant availability</li>
              <li>Geographic coverage</li>
              <li>Operational capacity</li>
              <li>Weather conditions</li>
              <li>Safety considerations</li>
            </ul>
            <p style={styles.paragraph}>
              MyHelpz reserves the right to decline, suspend, reschedule, or terminate
              services when necessary to protect customers, Assistants, or the integrity of the
              platform.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>12. Changes to Terms</h2>
            <p style={styles.paragraph}>
              MyHelpz may modify these Terms from time to time to reflect operational, legal,
              regulatory, or business changes.
            </p>
            <p style={styles.paragraph}>
              Updated Terms will be published on the MyHelpz website and mobile application.
              Continued use of the platform after such updates constitutes acceptance of the
              revised Terms.
            </p>
          </section>

          <div style={styles.commitment}>
            <span style={styles.strongText}>MyHelpz Commitment:</span> MyHelpz is India&apos;s
            On-Demand Human Assistance Platform dedicated to providing reliable, verified, and
            trustworthy human support whenever and wherever you need it. From home assistance
            and errands to travel, healthcare visits, shopping, and events, our mission is to
            make everyday life easier, safer, and more convenient.
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsPage;
